// The dome from above: ONE court plan for the whole site (Home, Book, Events).
// After the club's official Court Number map: courts 8 7 6 5 along the back, the mezzanine across the middle,
// courts 1 2 3 4 along the front, the entrance at the bottom centre.
//
// One look everywhere. Only the size, the ground, which courts are lit, and the selected or hover court vary.
// - Hall: one filled rounded shape, one tonal step from the ground. No outline, no seams.
// - Courts: 8 filled tiles in one orientation; the net runs across the full tile width, centred;
//   the number sits centred in the upper half, clear of the net.
// - Lit = Court Blue fill. Selected = --primary fill (ivory on navy) with --on-primary text.
// - Mezzanine: a filled bar between the rows (court tone on light grounds), labelled at 12px or more when the bar holds it.
// - Entrance: a small chevron centred below the front row, at least 8px clear of the tiles and the hall edge,
//   labelled at 12px or more whenever the mezzanine is labelled.
// - No text below 12px rendered.
//
// Canvas 2D, drawn on demand (one frame per state change, never a loop). DPR capped at 2.
// Animation is GSAP tweening `state`; with motion off every change is applied at once.
import gsap from "gsap";

export type Court = { id: number; free: boolean };

/** Plan units. The width is fixed; the height below the front row is computed in CSS pixels per size. */
const VW = 316;
const PAD = 20;
const W = 60;
const GX = 12;
const H = 104;
const GAP = 10;
const MZH = 28;
const R = 28;
const TR = 6;
const ROWS = [
  [8, 7, 6, 5],
  [1, 2, 3, 4],
];
const ORDER = [8, 7, 6, 5, 1, 2, 3, 4];
const LIFT_MAX = 0.05;

/** A court tile in plan units. */
export function courtRect(id: number) {
  const row = ROWS[0].includes(id) ? 0 : 1;
  const col = ROWS[row].indexOf(id);
  return { x: PAD + col * (W + GX), y: row === 0 ? PAD : PAD + H + GAP + MZH + GAP, w: W, h: H };
}
const MZ = { x: PAD, y: PAD + H + GAP, w: VW - 2 * PAD, h: MZH };
const ROWS_END = PAD + 2 * H + 2 * GAP + MZH;

/** True when a CSS colour (#rrggbb) is a light ground. */
function isLight(hex: string) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return false;
  const n = parseInt(m[1], 16);
  return (0.2126 * (n >> 16) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255 > 0.6;
}

const clamp = (lo: number, v: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Every size-dependent measure, in CSS pixels, for a plan drawn `cssW` wide. */
function layout(cssW: number, labels: boolean) {
  const s = cssW / VW;
  const lw = clamp(1.5, 1.5 * s, 2.5);
  const hw = clamp(6, 9 * s, 14);
  const hh = hw * 0.55;
  // the front row may lift by LIFT_MAX (selected or pulsed): keep 8px clear even then
  const liftPx = (LIFT_MAX / 2) * H * s;
  const clear = Math.max(8 + lw, 10 * s) + liftPx;
  // labels render at 12px minimum; they show whenever the mezzanine bar is tall enough to hold 12px text
  const lblPx = clamp(12, 13 * s, 16);
  const fits = labels && MZH * s >= 18;
  const entryLabel = fits;
  const lblGap = Math.max(4, 4 * s);
  const chevTop = ROWS_END * s + clear;
  let y = chevTop + 2 * hh;
  const lblY = y + lblGap + (lblPx * 1.25) / 2;
  if (entryLabel) y += lblGap + lblPx * 1.25;
  const height = y + Math.max(8 + lw, 10 * s);
  return {
    s, lw, hw, hh, chevTop, height, entryLabel, lblPx, lblY,
    mezzLabel: fits,
    mezzPx: clamp(12, 13 * s, 16),
    numPx: clamp(12, 20 * s, 44),
    hallR: Math.min(R * s, 48),
  };
}

type Palette = { light: boolean } & Record<"hall" | "mezz" | "tile" | "ink" | "inkMuted" | "inkSubtle" | "lit" | "onLit" | "sel" | "onSel" | "hover" | "font", string>;

export type PlanOptions = {
  /** false under ?calm or prefers-reduced-motion: every change applies at once. */
  motion?: boolean;
  /** What the plan sits on, so the hall is one tonal step from it. */
  base?: "page" | "surface";
  /** Mezzanine and Entrance labels, still only where they render at 12px or more. */
  labels?: boolean;
  label?: string;
  className?: string;
};

export class CourtPlan {
  readonly el: HTMLCanvasElement;
  /** GSAP state. reveal fades and settles the whole hall; per court (index id - 1): show, light, sel, hover, lift. */
  readonly state = {
    reveal: 1,
    mezz: 0,
    show: [1, 1, 1, 1, 1, 1, 1, 1],
    light: [0, 0, 0, 0, 0, 0, 0, 0],
    sel: [0, 0, 0, 0, 0, 0, 0, 0],
    hover: [0, 0, 0, 0, 0, 0, 0, 0],
    lift: [0, 0, 0, 0, 0, 0, 0, 0],
  };
  readonly motion: boolean;
  private ctx: CanvasRenderingContext2D;
  private opts: PlanOptions;
  private k!: Palette;
  private raf = 0;
  private ro: ResizeObserver;
  private lastKey = "";
  private cssW = 0;

  constructor(host: HTMLElement | HTMLCanvasElement, opts: PlanOptions = {}) {
    this.opts = opts;
    this.motion = opts.motion !== false;
    if (host instanceof HTMLCanvasElement) this.el = host;
    else {
      this.el = document.createElement("canvas");
      host.replaceChildren(this.el);
    }
    this.el.classList.add("court-plan");
    if (opts.className) this.el.classList.add(...opts.className.split(" "));
    this.el.setAttribute("role", "img");
    if (opts.label) this.el.setAttribute("aria-label", opts.label);
    Object.assign(this.el.style, { display: "block", width: "100%", aspectRatio: "auto" });
    this.ctx = this.el.getContext("2d")!;
    this.readColors();
    let pending = 0;
    this.ro = new ResizeObserver(() => {
      if (!pending) pending = requestAnimationFrame(() => ((pending = 0), this.fit()));
    });
    this.ro.observe(this.el);
    this.el.addEventListener("contextrestored", () => this.draw());
    document.fonts?.ready.then(() => this.draw());
    this.fit();
  }

  /** Re-read the ground's tokens (call after moving the plan to another ground). */
  readColors() {
    const cs = getComputedStyle(this.el);
    const v = (n: string) => cs.getPropertyValue(`--${n}`).trim();
    const ivory = this.el.closest("[data-ground]")?.getAttribute("data-ground") === "ivory" || isLight(v("page"));
    const onSurface = this.opts.base === "surface";
    this.k = {
      light: ivory,
      hall: ivory ? v("layer-1") : onSurface ? v("page") : v("surface"),
      // on light grounds the mezzanine takes the court tone, never a blank white bar
      mezz: ivory ? v("layer-2") : onSurface ? v("layer-1") : v("page"),
      tile: ivory ? v("layer-2") : onSurface ? v("layer-2") : v("layer-1"),
      ink: v("ink"),
      inkMuted: v("ink-muted"),
      inkSubtle: v("ink-subtle"),
      lit: v("accent-fill"),
      onLit: v("on-accent-fill"),
      sel: v("primary"),
      onSel: v("on-primary"),
      hover: v("accent"),
      font: v("font-text") || "Inter, system-ui, sans-serif",
    };
  }

  setLabel(label: string) {
    this.el.setAttribute("aria-label", label);
  }

  private fit() {
    const w = this.el.clientWidth;
    if (!w) return;
    this.cssW = w;
    const L = layout(w, this.opts.labels !== false);
    const hPx = Math.round(L.height);
    if (this.el.style.height !== `${hPx}px`) this.el.style.height = `${hPx}px`;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const bw = Math.round(w * dpr), bh = Math.round(hPx * dpr);
    if (this.el.width !== bw || this.el.height !== bh) {
      this.el.width = bw;
      this.el.height = bh;
    }
    this.draw();
  }

  /** Redraw on the next frame (call from GSAP onUpdate). */
  queue = () => {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      this.draw();
    });
  };

  private tween(target: object, vars: gsap.TweenVars) {
    if (!this.motion) {
      // calm: write the end state straight into the state object, so no GSAP call wakes the sleeping ticker
      gsap.killTweensOf(target);
      Object.assign(target, Object.fromEntries(Object.entries(vars).filter(([k]) => !["duration", "delay", "ease", "stagger", "overwrite", "onUpdate", "onComplete"].includes(k))));
      this.queue();
      return;
    }
    gsap.to(target, { overwrite: "auto", ...vars, onUpdate: this.queue });
  }

  /** Intro: the hall fades and settles in; with `courts`, the tiles then appear one after another in court-number order. */
  intro(o: { courts?: boolean; delay?: number } = {}) {
    const st = this.state;
    if (!this.motion) {
      st.reveal = 1;
      st.show = st.show.map(() => 1);
      return this.queue();
    }
    st.reveal = 0;
    if (o.courts) st.show.forEach((_, i) => (st.show[i] = 0));
    this.queue();
    gsap.to(st, { reveal: 1, duration: 0.9, delay: o.delay ?? 0, ease: "power2.out", onUpdate: this.queue });
    if (o.courts)
      st.show.forEach((_, i) =>
        gsap.to(st.show, { [i]: 1, duration: 0.7, delay: (o.delay ?? 0) + 0.25 + i * 0.07, ease: "power3.out", onUpdate: this.queue }),
      );
  }

  /**
   * Light courts. `levels[id - 1]` is 0..1. With motion, newly lit courts light one after another
   * in `order` (default: the plan's reading order 8 7 6 5 1 2 3 4).
   */
  lights(levels: number[], o: { order?: number[]; stagger?: number; delay?: number; duration?: number } = {}) {
    const st = this.state;
    const order = o.order ?? ORDER;
    let n = 0;
    order.forEach((id) => {
      const i = id - 1;
      const to = levels[i] ?? 0;
      const rising = to > st.light[i];
      const delay = (o.delay ?? 0) + (rising ? n++ * (o.stagger ?? 0.09) : 0);
      this.tween(st.light, { [i]: to, duration: o.duration ?? (rising ? 0.45 : 0.3), delay, ease: "power3.out" });
    });
  }

  /** The selected court (ivory on navy) or none. */
  select(id: number | null) {
    const st = this.state;
    st.sel.forEach((_, i) => {
      const on = i + 1 === id ? 1 : 0;
      this.tween(st.sel, { [i]: on, duration: 0.45, ease: "power3.out" });
      this.tween(st.lift, { [i]: on ? 0.03 : 0, duration: 0.5, delay: on ? 0.1 : 0, ease: "back.out(2)" });
    });
  }

  /** Hover state on one court (pointer over a free court on an interactive plan). */
  hover(id: number | null) {
    const st = this.state;
    st.hover.forEach((_, i) => {
      const on = i + 1 === id ? 1 : 0;
      if (st.hover[i] !== on) this.tween(st.hover, { [i]: on, duration: 0.2, ease: "power2.out" });
    });
  }

  /** Availability: free courts lit, the chosen court selected. A new availability picture relights one by one. */
  set(courts: Court[] | null, selected: number | null, label?: string) {
    if (label) this.setLabel(label);
    const free = new Map((courts ?? []).map((c) => [c.id, c.free]));
    const levels = Array.from({ length: 8 }, (_, i) => (courts && free.get(i + 1) ? 1 : 0));
    const key = levels.join("");
    const relight = key !== this.lastKey;
    this.lastKey = key;
    if (relight && this.motion) {
      // light from dark, one after another
      ORDER.forEach((id) => levels[id - 1] && (this.state.light[id - 1] = 0));
      gsap.killTweensOf(this.state.light);
    }
    this.lights(levels, { delay: relight ? 0.12 : 0, stagger: relight ? 0.09 : 0 });
    this.select(selected);
  }

  /** One pulse on a court: lift, settle back. */
  pulse(id: number) {
    const st = this.state;
    if (!this.motion || id < 1 || id > 8) return;
    const i = id - 1;
    const rest = st.sel[i] > 0.5 ? 0.03 : 0;
    gsap.timeline({ onUpdate: this.queue })
      .to(st.lift, { [i]: LIFT_MAX, duration: 0.35, ease: "power2.out" })
      .to(st.lift, { [i]: rest, duration: 0.6, ease: "power2.inOut" });
  }

  /** Client point of a court's centre. */
  courtPoint(id: number) {
    const r = this.el.getBoundingClientRect();
    const s = r.width / VW;
    const c = courtRect(id);
    return { x: r.left + (c.x + c.w / 2) * s, y: r.top + (c.y + c.h / 2) * s };
  }

  /** The court under a client point, if any. */
  courtAt(cx: number, cy: number): number | null {
    const r = this.el.getBoundingClientRect();
    const s = r.width / VW;
    const px = (cx - r.left) / s, py = (cy - r.top) / s;
    for (let id = 1; id <= 8; id++) {
      const c = courtRect(id);
      if (px >= c.x && px <= c.x + c.w && py >= c.y && py <= c.y + c.h) return id;
    }
    return null;
  }

  destroy() {
    this.ro.disconnect();
    if (this.raf) cancelAnimationFrame(this.raf);
    const st = this.state;
    gsap.killTweensOf([st, st.show, st.light, st.sel, st.hover, st.lift]);
  }

  draw() {
    const w = this.cssW || this.el.clientWidth;
    if (!w || !this.el.width) return;
    const c = this.ctx;
    const k = this.k;
    const st = this.state;
    const L = layout(w, this.opts.labels !== false);
    const s = L.s;
    const dpr = this.el.width / w;
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.clearRect(0, 0, this.el.width, this.el.height);
    const A = clamp(0, st.reveal, 1);
    if (A <= 0) return;
    // everything below is in CSS pixels; the whole hall settles from 0.96 as it reveals
    const z = 0.96 + 0.04 * A;
    c.setTransform(dpr * z, 0, 0, dpr * z, (dpr * w * (1 - z)) / 2, (dpr * L.height * (1 - z)) / 2);
    c.textAlign = "center";
    c.textBaseline = "middle";
    const font = (wt: number, px: number) => `${wt} ${px}px ${k.font}`;

    // hall: one filled rounded shape
    c.globalAlpha = A;
    c.beginPath();
    c.roundRect(0, 0, w, L.height, L.hallR);
    c.fillStyle = k.hall;
    c.fill();

    // mezzanine: a filled bar; lit with the whole dome
    c.beginPath();
    c.roundRect(MZ.x * s, MZ.y * s, MZ.w * s, MZ.h * s, TR * s);
    c.fillStyle = k.mezz;
    c.fill();
    const m = clamp(0, st.mezz, 1);
    if (m > 0) {
      c.globalAlpha = A * m;
      c.fillStyle = k.lit;
      c.fill();
      c.globalAlpha = A;
    }
    if (L.mezzLabel) {
      c.font = font(400, L.mezzPx);
      c.fillStyle = m > 0.5 ? k.onLit : k.light ? k.ink : k.inkMuted;
      if (k.light && m <= 0.5) c.globalAlpha = A * 0.6;
      c.fillText("Mezzanine", w / 2, (MZ.y + MZ.h / 2) * s + 0.5);
      c.globalAlpha = A;
    }

    // entrance: a small chevron centred below the front row, pointing into the hall
    c.strokeStyle = k.inkMuted;
    c.lineWidth = L.lw;
    c.lineCap = "round";
    c.lineJoin = "round";
    c.beginPath();
    c.moveTo(w / 2 - L.hw, L.chevTop + 2 * L.hh);
    c.lineTo(w / 2, L.chevTop);
    c.lineTo(w / 2 + L.hw, L.chevTop + 2 * L.hh);
    c.stroke();
    if (L.entryLabel) {
      c.font = font(400, L.lblPx);
      c.fillStyle = k.inkMuted;
      c.fillText("Entrance", w / 2, L.lblY);
    }

    // courts
    const netW = clamp(1, 1.25 * s, 2);
    for (let id = 1; id <= 8; id++) {
      const i = id - 1;
      const show = clamp(0, st.show[i], 1);
      if (show <= 0) continue;
      const r = courtRect(id);
      const x = r.x * s, y = r.y * s, tw = r.w * s, th = r.h * s;
      const light = clamp(0, st.light[i], 1);
      const sel = clamp(0, st.sel[i], 1);
      const hov = clamp(0, st.hover[i], 1) * (1 - sel);
      c.save();
      c.globalAlpha = A * show;
      // tiles rise 10px into place as they appear; the chosen one lifts slightly
      c.translate(0, (1 - show) * 10);
      const zl = 1 + st.lift[i];
      const cx = x + tw / 2, cy = y + th / 2;
      c.translate(cx, cy);
      c.scale(zl, zl);
      c.translate(-cx, -cy);
      c.beginPath();
      c.roundRect(x, y, tw, th, TR * s);
      c.fillStyle = k.tile;
      c.fill();
      const layer = (alpha: number, fill: string) => {
        if (alpha <= 0) return;
        c.globalAlpha = A * show * alpha;
        c.fillStyle = fill;
        c.fill();
      };
      layer(light, k.lit);
      layer(hov, k.hover);
      layer(sel, k.sel);
      c.globalAlpha = A * show;
      const ink = sel > 0.5 ? k.onSel : hov > 0.5 ? k.onSel : light > 0.5 ? k.onLit : k.inkSubtle;
      // net: across the full tile width, centred
      c.beginPath();
      c.moveTo(x, y + th / 2);
      c.lineTo(x + tw, y + th / 2);
      c.strokeStyle = ink;
      c.globalAlpha = A * show * (ink === k.inkSubtle ? 0.45 : 0.5);
      c.lineWidth = netW;
      c.lineCap = "butt";
      c.stroke();
      // number: centred in the upper half, clear of the net
      c.globalAlpha = A * show;
      c.font = font(600, L.numPx);
      c.fillStyle = ink;
      c.fillText(String(id), x + tw / 2, y + th / 4 + 0.5);
      c.restore();
    }
    c.globalAlpha = 1;
  }
}
